// Solo Auth: la SPA nunca habla con Postgres directo; todo pasa por la API en Hono.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { DOMINIOS_PERMITIDOS } from "./dominios";

export type ClienteAuth = SupabaseClient;

export function crearClienteAuth(url: string, llaveAnon: string): ClienteAuth {
  return createClient(url, llaveAnon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  });
}

/** Login con Google restringido al Workspace de Vanguardia (`hd`); el servidor vuelve a verificar el dominio. */
export async function entrarConGoogle(cliente: ClienteAuth, redirigirA?: string) {
  const origen = (globalThis as { location?: { origin: string } }).location?.origin;
  return cliente.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: redirigirA ?? origen,
      queryParams: { hd: DOMINIOS_PERMITIDOS[0], prompt: "select_account" },
    },
  });
}

export async function salir(cliente: ClienteAuth) {
  await cliente.auth.signOut();
}

/** Token para el encabezado Authorization: Bearer <jwt>, o null si no hay sesión. */
export async function tokenActual(cliente: ClienteAuth): Promise<string | null> {
  const { data } = await cliente.auth.getSession();
  return data.session?.access_token ?? null;
}

// ─── Ligas que llegan por correo ────────────────────────────────────────────

/** Tipos de liga que manda Supabase con token_hash (las plantillas de packages/auth/supabase). */
export type TipoLiga = "invite" | "magiclink" | "signup" | "recovery" | "email_change" | "email";
const TIPOS_LIGA: readonly TipoLiga[] = ["invite", "magiclink", "signup", "recovery", "email_change", "email"];

/** Lo que trae la URL en la que aterriza una persona al abrir una liga. */
export type Liga =
  /** Plantillas de correo (invitación, liga mágica, recuperación, cambio de correo): `?token_hash=…&type=…`. */
  | { clase: "token_hash"; tokenHash: string; tipo: TipoLiga }
  /** Flujo PKCE (Google, o liga pedida desde este mismo navegador): `?code=…`. */
  | { clase: "code"; code: string }
  /** Flujo implícito: `#access_token=…&refresh_token=…`. */
  | { clase: "implicito"; accessToken: string; refreshToken: string }
  /** Supabase ya rechazó la liga antes de llegar aquí: `#error_code=otp_expired…` o `?error=…`. */
  | { clase: "error"; codigo: string | null; descripcion: string | null }
  /** La URL no trae nada: alguien entró a la ruta sin liga. */
  | { clase: "nada" };

/** Lee la liga de una URL. Puro: se prueba sin navegador. */
export function leerLiga(url: URL | string): Liga {
  const u = typeof url === "string" ? new URL(url) : url;
  const q = u.searchParams;
  const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
  const codigoError = frag.get("error_code") ?? q.get("error_code") ?? frag.get("error") ?? q.get("error");
  const descripcionError = frag.get("error_description") ?? q.get("error_description");
  if (codigoError || descripcionError)
    return { clase: "error", codigo: codigoError, descripcion: descripcionError };
  const tokenHash = q.get("token_hash");
  const tipo = q.get("type");
  if (tokenHash && tipo && (TIPOS_LIGA as readonly string[]).includes(tipo))
    return { clase: "token_hash", tokenHash, tipo: tipo as TipoLiga };
  const code = q.get("code");
  if (code) return { clase: "code", code };
  const accessToken = frag.get("access_token");
  const refreshToken = frag.get("refresh_token");
  if (accessToken && refreshToken) return { clase: "implicito", accessToken, refreshToken };
  return { clase: "nada" };
}

/** ¿Una liga expiró o ya se usó? Supabase lo dice de varias formas. */
export function ligaVencida(codigo: string | null, mensaje: string | null): boolean {
  return /otp_expired|expired|invalid|already|used/i.test(`${codigo ?? ""} ${mensaje ?? ""}`);
}

/**
 * Abre la sesión a partir de la liga. Devuelve la sesión o null si la liga no
 * traía nada; lanza el error de Supabase si la liga no sirve. No toca la URL:
 * eso lo decide la pantalla (`limpiarLigaDeUrl`).
 */
export async function abrirSesionDesdeLiga(cliente: ClienteAuth, liga: Liga) {
  switch (liga.clase) {
    case "token_hash": {
      const { data, error } = await cliente.auth.verifyOtp({ token_hash: liga.tokenHash, type: liga.tipo });
      if (error) throw error;
      return data.session;
    }
    case "code": {
      const { data, error } = await cliente.auth.exchangeCodeForSession(liga.code);
      if (error) throw error;
      return data.session;
    }
    case "implicito": {
      const { data, error } = await cliente.auth.setSession({
        access_token: liga.accessToken,
        refresh_token: liga.refreshToken,
      });
      if (error) throw error;
      return data.session;
    }
    case "error":
      throw Object.assign(new Error(liga.descripcion ?? liga.codigo ?? "La liga no sirve."), {
        code: liga.codigo ?? undefined,
      });
    case "nada":
      return null;
  }
}

/** La liga sirve una vez: que no quede en el historial ni en la barra. */
export function limpiarLigaDeUrl() {
  const w = globalThis as {
    history?: { replaceState: (estado: unknown, titulo: string, url: string) => void };
    location?: { pathname: string };
  };
  if (w.history && w.location) w.history.replaceState(null, "", w.location.pathname);
}

/**
 * La ruta /auth/confirmar del portal: lee `?token_hash=…&type=…` (o `?code=` /
 * `#access_token=`), abre la sesión y limpia la URL. Devuelve null si no había liga.
 */
export async function confirmarDesdeCorreo(cliente: ClienteAuth) {
  const w = globalThis as { location?: { href: string } };
  if (!w.location) return null;
  const liga = leerLiga(w.location.href);
  if (liga.clase === "nada") return null;
  try {
    return await abrirSesionDesdeLiga(cliente, liga);
  } finally {
    limpiarLigaDeUrl();
  }
}
